// =============================================================================
// Bureau de Matthieu — actions principales par type de fiche (T-276). Chaque action part d'un CLIC ; aucune n'envoie de mail :
// « Rédiger » et « Demander des pièces » préparent une réponse dans la file de validation, que JJ relit puis valide comme les autres.
// Tout passe par les fonctions de service du serveur (garde tenant, registre matthieu_echanges). Couleurs : tokens var(--oh-*) uniquement.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Forward, PenLine, UserPlus, Zap } from "lucide-react";

import { OhCard, OhCardHeader, OhField, OhInput, OhSelect, OhStatusNote } from "../../../components/ui/oh-charte";
import * as api from "./bureauAmoApi";
import * as matthieu from "./matthieuApi";
import Bouton from "./boutonBureau";

export const MATTHIEU_NON_CONNECTE = "Matthieu n'est pas encore connecté";
const muted = { color: "var(--oh-text-muted)", fontSize: 13, margin: 0 };
const ZONE = { width: "100%", padding: "var(--oh-space-3)", borderRadius: "var(--oh-radius-control)", border: "0.5px solid var(--oh-border)", background: "var(--oh-bg-elevated)", color: "var(--oh-text)", fontSize: 14, lineHeight: 1.6 };

export const messageEchec = (e, defaut) => {
  if (e?.status === 409) return "La fiche a changé entre-temps : relisez-la avant de recommencer.";
  return typeof e?.detail === "string" ? e.detail : defaut;
};

// null = en cours de lecture · true / false = clé Claude enregistrée ou non. Une lecture impossible vaut « non connecté » (jamais d'appel inutile).
export function useMatthieuConnecte(actif = true) {
  const [connecte, setConnecte] = useState(null);
  useEffect(() => {
    if (!actif) return undefined;
    let vivant = true;
    matthieu.lireConfig().then(
      (c) => { if (vivant) setConnecte(Boolean(c?.cle_enregistree)); },
      () => { if (vivant) setConnecte(false); },
    );
    return () => { vivant = false; };
  }, [actif]);
  return connecte;
}

export function BoutonRediger({ connecte, occupe, onClick, testid = "bureau-amo-rediger" }) {
  const indisponible = connecte === false;
  return (
    <div className="space-y-1" data-testid={`${testid}-bloc`}>
      <Bouton type="secondaire" data-testid={testid} disabled={occupe || connecte !== true} aria-busy={occupe}
        title={indisponible ? MATTHIEU_NON_CONNECTE : undefined} onClick={onClick}>
        <PenLine className="w-4 h-4 inline mr-1" aria-hidden="true" />{occupe ? "Ouverture…" : "Rédiger une réponse avec Matthieu"}
      </Bouton>
      {indisponible && <p style={muted} data-testid={`${testid}-non-connecte`}>{MATTHIEU_NON_CONNECTE}. Un administrateur doit renseigner la clé Claude dans Intégrations.</p>}
      {connecte === true && <p style={muted}>Matthieu propose un texte dans la fiche ; vous le modifiez puis vous validez. Rien ne part avant votre clic.</p>}
    </div>
  );
}

function Panneau({ titre, testid, children }) {
  return (
    <div data-testid={testid} className="space-y-2"
      style={{ background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)" }}>
      <p style={{ margin: 0, fontWeight: 500, color: "var(--oh-text)", fontSize: 14 }}>{titre}</p>
      {children}
    </div>
  );
}

// ───────────────────────────── Transférer à un utilisateur du tenant ─────────────────────────────
export function Transferer({ type, id, transfereA, desactive, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [liste, setListe] = useState({ chargement: false, erreur: null, items: null });
  const [dest, setDest] = useState("");
  const [note, setNote] = useState("");
  const [occupe, setOccupe] = useState(false);

  useEffect(() => {
    if (!ouvert || liste.items) return undefined;
    let vivant = true;
    setListe({ chargement: true, erreur: null, items: null });
    api.utilisateurs().then(
      (r) => { if (vivant) setListe({ chargement: false, erreur: null, items: r.items || [] }); },
      (e) => { if (vivant) setListe({ chargement: false, erreur: e, items: null }); },
    );
    return () => { vivant = false; };
  }, [ouvert, liste.items]);

  const envoyer = async () => {
    setOccupe(true);
    try {
      const r = await api.transferer(type, id, dest, note.trim());
      toast.success(`Fiche confiée à ${r.transfere_a?.nom || "la personne choisie"}.`);
      setOuvert(false); setDest(""); setNote("");
      if (onFait) await onFait();
    } catch (e) {
      toast.error(messageEchec(e, "Transfert impossible."));
    } finally {
      setOccupe(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="bureau-amo-transfert">
      {transfereA?.nom && (
        <p style={{ ...muted, color: "var(--oh-text)" }} data-testid="bureau-amo-transfere-a">
          Confiée à {transfereA.nom}{transfereA.note ? ` — « ${transfereA.note} »` : ""}.
        </p>
      )}
      <Bouton data-testid="bureau-amo-transferer" disabled={desactive || occupe} aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
        <Forward className="w-4 h-4 inline mr-1" aria-hidden="true" />Transférer à…
      </Bouton>
      {ouvert && (
        <Panneau titre="Confier cette fiche à un collègue" testid="bureau-amo-transfert-formulaire">
          {liste.chargement && <p style={muted} role="status" data-testid="bureau-amo-transfert-chargement">Chargement des utilisateurs…</p>}
          {liste.erreur && <OhStatusNote variant="error" title="Utilisateurs indisponibles">Réessayez dans un instant.</OhStatusNote>}
          {liste.items && liste.items.length === 0 && (
            <p style={muted} data-testid="bureau-amo-transfert-vide">Aucun autre utilisateur dans votre organisation.</p>
          )}
          {liste.items && liste.items.length > 0 && (
            <>
              <OhField label="Transférer à" htmlFor="bureau-amo-transfert-dest">
                <OhSelect id="bureau-amo-transfert-dest" data-testid="bureau-amo-transfert-dest" value={dest} onChange={(e) => setDest(e.target.value)}>
                  <option value="">Choisir une personne</option>
                  {liste.items.map((u) => <option key={u.user_id} value={u.user_id}>{u.nom}</option>)}
                </OhSelect>
              </OhField>
              <OhField label="Note pour cette personne (facultatif)" htmlFor="bureau-amo-transfert-note" hint="500 caractères au plus. Aucun mail n'est envoyé.">
                <textarea id="bureau-amo-transfert-note" data-testid="bureau-amo-transfert-note" rows={3} maxLength={500} value={note} style={ZONE}
                  onChange={(e) => setNote(e.target.value)} />
              </OhField>
              <div className="flex flex-wrap gap-2">
                <Bouton type="secondaire" data-testid="bureau-amo-transfert-confirmer" disabled={occupe || !dest} onClick={envoyer}>
                  {occupe ? "Transfert…" : "Transférer la fiche"}
                </Bouton>
                <Bouton data-testid="bureau-amo-transfert-annuler" onClick={() => setOuvert(false)}>Annuler</Bouton>
              </div>
            </>
          )}
        </Panneau>
      )}
    </div>
  );
}

// ───────────────────────────── Instructeurs : compte rendu + demande de pièces ─────────────────────────────
export function CompteRendu({ id, desactive, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [texte, setTexte] = useState("");
  const [occupe, setOccupe] = useState(false);
  const ajouter = async () => {
    setOccupe(true);
    try {
      await api.ajouterAuDossier(id, texte.trim());
      toast.success("Compte rendu ajouté au dossier.");
      setOuvert(false); setTexte("");
      if (onFait) await onFait();
    } catch (e) {
      toast.error(messageEchec(e, "Le compte rendu n'a pas pu être ajouté."));
    } finally {
      setOccupe(false);
    }
  };
  return (
    <div className="space-y-2">
      <Bouton data-testid="bureau-amo-ajouter-dossier" disabled={desactive || occupe} aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
        <FileText className="w-4 h-4 inline mr-1" aria-hidden="true" />Ajouter au dossier
      </Bouton>
      {ouvert && (
        <Panneau titre="Compte rendu à ajouter au dossier" testid="bureau-amo-compte-rendu">
          <OhField label="Compte rendu" htmlFor="bureau-amo-compte-rendu-texte" hint="Visible par l'équipe dans les commentaires du dossier. 2000 caractères au plus.">
            <textarea id="bureau-amo-compte-rendu-texte" data-testid="bureau-amo-compte-rendu-texte" rows={5} maxLength={2000} value={texte} style={ZONE}
              onChange={(e) => setTexte(e.target.value)} />
          </OhField>
          <div className="flex flex-wrap gap-2">
            <Bouton type="secondaire" data-testid="bureau-amo-compte-rendu-ajouter" disabled={occupe || !texte.trim()} onClick={ajouter}>
              {occupe ? "Ajout…" : "Ajouter au dossier"}
            </Bouton>
            <Bouton data-testid="bureau-amo-compte-rendu-annuler" onClick={() => setOuvert(false)}>Annuler</Bouton>
          </div>
        </Panneau>
      )}
    </div>
  );
}

export function DemanderPieces({ id, desactive, onOuvrirProposition }) {
  const [occupe, setOccupe] = useState(false);
  const lancer = async () => {
    setOccupe(true);
    try {
      const r = await api.demanderPieces(id);
      const n = (r.pieces || []).length;
      toast.success(r.deja
        ? "La demande de pièces est déjà préparée : elle est ouverte."
        : `Demande préparée (${n} pièce${n > 1 ? "s" : ""}) : relisez-la, rien n'est parti.`);
      if (onOuvrirProposition) onOuvrirProposition(r.proposition_id);
    } catch (e) {
      toast.error(messageEchec(e, "La demande de pièces n'a pas pu être préparée."));
    } finally {
      setOccupe(false);
    }
  };
  return (
    <div className="space-y-1">
      <Bouton type="secondaire" data-testid="bureau-amo-demander-pieces" disabled={desactive || occupe} aria-busy={occupe} onClick={lancer}>
        <UserPlus className="w-4 h-4 inline mr-1" aria-hidden="true" />{occupe ? "Préparation…" : "Demander des pièces au client"}
      </Bouton>
      <p style={muted}>Crée une réponse au client avec la liste des pièces manquantes de la checklist du dossier. Vous la relisez avant tout envoi.</p>
    </div>
  );
}

// ───────────────────────────── Leads : créer le dossier ─────────────────────────────
const CHAMPS_DOSSIER = [
  ["nom", "Nom", "text", 80], ["prenom", "Prénom", "text", 80], ["email", "E-mail", "email", 320],
  ["telephone", "Téléphone", "tel", 30], ["code_postal", "Code postal", "text", 5],
];

export function CreerDossier({ id, dejaDossierId, onFait }) {
  const [ouvert, setOuvert] = useState(false);
  const [etat, setEtat] = useState({ chargement: false, erreur: null, donnees: null });
  const [champs, setChamps] = useState({});
  const [occupe, setOccupe] = useState(false);
  const [echec, setEchec] = useState(null);

  const charger = useCallback(async () => {
    setEtat({ chargement: true, erreur: null, donnees: null });
    try {
      const d = await api.dossierACreer(id);
      setEtat({ chargement: false, erreur: null, donnees: d });
      setChamps(d.champs || {});
    } catch (e) {
      setEtat({ chargement: false, erreur: e, donnees: null });
    }
  }, [id]);
  useEffect(() => { if (ouvert) charger(); }, [ouvert, charger]);

  const creer = async () => {
    setOccupe(true);
    setEchec(null);
    try {
      const r = await api.creerDossier(id, etat.donnees.version, champs);
      toast.success(r.deja ? `Le dossier ${r.reference} existait déjà.` : `Dossier ${r.reference} créé.`);
      setOuvert(false);
      if (onFait) await onFait();
    } catch (e) {
      const message = messageEchec(e, "Le dossier n'a pas pu être créé.");
      toast.error(message);
      if (e?.status === 409) await charger();
      setEchec(message);
    } finally {
      setOccupe(false);
    }
  };

  if (dejaDossierId) {
    return <p style={{ ...muted, color: "var(--oh-text)" }} data-testid="bureau-amo-dossier-cree">Un dossier a déjà été créé à partir de ce lead.</p>;
  }
  const d = etat.donnees;
  return (
    <div className="space-y-2">
      <Bouton type="secondaire" data-testid="bureau-amo-creer-dossier" disabled={occupe} aria-expanded={ouvert} onClick={() => setOuvert((o) => !o)}>
        <Zap className="w-4 h-4 inline mr-1" aria-hidden="true" />Créer le dossier
      </Bouton>
      {ouvert && (
        <Panneau titre="Dossier à créer à partir du lead" testid="bureau-amo-creer-dossier-formulaire">
          {etat.chargement && <p style={muted} role="status" data-testid="bureau-amo-creer-dossier-chargement">Lecture du lead…</p>}
          {etat.erreur && <OhStatusNote variant="error" data-testid="bureau-amo-creer-dossier-erreur" title="Préparation impossible">{messageEchec(etat.erreur, "Réessayez dans un instant.")}</OhStatusNote>}
          {echec && <OhStatusNote variant="error" role="alert" data-testid="bureau-amo-creer-dossier-echec" title="Le dossier n'a pas été créé">{echec} Corrigez si besoin, puis réessayez.</OhStatusNote>}
          {d?.deja_dossier_id && <OhStatusNote variant="info" title="Dossier déjà créé">Ce lead a déjà un dossier : rien de plus à faire.</OhStatusNote>}
          {d && !d.deja_dossier_id && (
            <>
              <p style={muted}>Champs préremplis depuis le mail : corrigez-les avant de créer.</p>
              {CHAMPS_DOSSIER.map(([cle, libelle, genre, max]) => (
                <OhField key={cle} label={libelle} htmlFor={`bureau-amo-dossier-champ-${cle}`}>
                  <OhInput id={`bureau-amo-dossier-champ-${cle}`} data-testid={`bureau-amo-dossier-champ-${cle}`} type={genre} maxLength={max}
                    value={champs[cle] || ""} onChange={(e) => setChamps((c) => ({ ...c, [cle]: e.target.value }))} />
                </OhField>
              ))}
              <div className="flex flex-wrap gap-2">
                <Bouton type="secondaire" data-testid="bureau-amo-creer-dossier-valider" disabled={occupe || !(champs.nom || "").trim()} onClick={creer}>
                  {occupe ? "Création…" : "Créer le dossier"}
                </Bouton>
                <Bouton data-testid="bureau-amo-creer-dossier-annuler" onClick={() => setOuvert(false)}>Annuler</Bouton>
              </div>
            </>
          )}
        </Panneau>
      )}
    </div>
  );
}

// ───────────────────────────── Publicités : toujours classer cet expéditeur ─────────────────────────────
export function ToujoursClasser({ fiche, desactive, onFait }) {
  const [occupe, setOccupe] = useState(false);
  const lancer = async () => {
    setOccupe(true);
    try {
      const r = await api.toujoursClasser(fiche.id, fiche.version);
      const regleId = r.regle?.id;
      toast.success(`Règle posée : les prochains mails de ${r.regle?.adresse || "cet expéditeur"} seront classés.`, {
        duration: 12000,
        action: regleId ? {
          label: "Annuler",
          onClick: async () => {
            try { await api.retirerRegle(regleId); toast.success("Règle retirée."); if (onFait) await onFait(); }
            catch (e) { toast.error(messageEchec(e, "La règle n'a pas pu être retirée.")); }
          },
        } : undefined,
      });
      if (onFait) await onFait();
    } catch (e) {
      toast.error(messageEchec(e, "La règle n'a pas pu être posée."));
    } finally {
      setOccupe(false);
    }
  };
  return (
    <div className="space-y-1">
      <Bouton type="secondaire" data-testid="bureau-amo-toujours-classer" disabled={desactive || occupe || Boolean(fiche.regle_expediteur_id)} aria-busy={occupe} onClick={lancer}>
        {occupe ? "Pose de la règle…" : fiche.regle_expediteur_id ? "Règle déjà posée" : "Toujours classer cet expéditeur"}
      </Bouton>
      <p style={muted}>S'applique à cette adresse exacte, jamais à son domaine. Vous pouvez retirer la règle juste après avoir cliqué.</p>
    </div>
  );
}

export function CarteActions({ titre, testid, children }) {
  return (
    <OhCard data-testid={testid}>
      <OhCardHeader title={titre} />
      <div className="space-y-3" style={{ marginTop: "var(--oh-space-3)" }}>{children}</div>
    </OhCard>
  );
}
